// addAppPrompt.js — the app builder starts a NEW build thread with a waiting
// text as its first message, ONCE (wt/presets CONTRACT §5 WIZARD).
//
// FREE TEXT NEVER IN A URL (CONTRACT §1): an in-app sender PARKS the text and
// routes to the bare `/add-app` — the welcome's "Build my app"
// (functions/onboardingPresets.js onboardingBuildIdea → addAppPromptPark).
// `?prompt=<text>` and `#prompt=<text>` stay readable for INBOUND links only
// (an external link, an old bookmark, Fly's hand-over
// `/w/<org>/<ws>/add-app?prompt=<idea>&fly=`, fly docs/SYMBOLS.md §5): read
// once, parked, and taken out of the address bar in the same tick.
//
// THE PARK — the landing hand-off's own order (functions/inboundPrompt.js):
// the text leaves the address bar only once it is held where a slower step
// can still reach it. The builder's mount reads it (addAppPromptTake), parks
// it in THIS tab's sessionStorage, then drops `prompt` — from the query AND
// the fragment — and `fly`, and a `thread` that would bind another app, from
// the address bar, in the same call (history and trackers read
// `location.href` / `hash`). The send waits for the builder's transport
// (`root.bridgeStatus === 'open'`) and takes the park ONE time
// (addAppPromptFlush): a reload in between finds the park, not the param, so
// the text is sent once. Where nothing can be parked (a sandboxed frame, a
// full quota) the text is held on the root for this page only and the param
// stays until the send, so a reload still has it.
//
// The shell root's boot hand-off (app.js, `?prompt=` for the HOME composer)
// leaves this route alone (inboundPromptForBuilder): the home composer never
// also takes it.
//
// `fly` — a Fly arrival: Make it real answered the welcome's questions already,
// so the full wizard never opens over the build thread. The builder only MARKS
// the load (functions/onboarding.js onboardingNoteFlyArrival — the shell's boot
// marks it too, before this mount strips `fly`) and decides nothing: Fly's
// `pending` record is what owes the person the one "Your workspace is ready"
// screen, and that screen opens over the build thread without touching it
// (functions/onboardingReady.js) — the park below and the send are the
// builder's alone.
//
// FRANK: every export is self-contained (FA204); siblings through `this.call`.

// The builder's params in a query string (and, for inbound links, a fragment)
// → { text, fly }: `text` the trimmed prompt (2000 characters at most, the
// landing's own bound) or null; `fly` true when Fly's hand-over marked it
// (`fly` present, whatever its value). The query's prompt wins when both carry
// one. A fragment is read only when it is param-shaped and names `prompt` or
// `fly` — a plain anchor (`#main-content`) is not a param. `URLSearchParams`
// decodes once — a second decode would mangle a typed '%'.
export function addAppPromptRead(search, hash) {
  const q = new URLSearchParams(typeof search === 'string' ? search : '')
  const frag = typeof hash === 'string' ? hash.replace(/^#/, '') : ''
  const h = /(^|&)(prompt|fly)(=|&|$)/.test(frag) ? new URLSearchParams(frag) : null
  const raw = q.get('prompt') || (h && h.get('prompt')) || ''
  const text = raw ? raw.trim().slice(0, 2000) : ''
  return { text: text || null, fly: q.has('fly') || !!(h && h.has('fly')) }
}

// THE PARK's one writer — the builder's mount (a `?prompt=` / `#prompt=`
// link) and the welcome's "Build my app" both hold a text here, in the shape
// addAppPromptTake / addAppPromptFlush read: this tab's sessionStorage
// `symbols_add_app_prompt` = `{ text, at }`; where storage is refused (a
// sandboxed frame, a full quota), the root holds it for this page
// (`__addAppPromptHeld`). → 'parked' | 'held' | false (no text, no root).
export function addAppPromptPark(text, root) {
  const el = this
  const r = root || (el && el.state && el.state.root) || null
  const t = String(text == null ? '' : text)
    .trim()
    .slice(0, 2000)
  if (!t) return false
  const w = typeof window !== 'undefined' ? window : null
  try {
    const store = w ? w.sessionStorage : null
    if (store) {
      store.setItem('symbols_add_app_prompt', JSON.stringify({ text: t, at: Date.now() }))
      return 'parked'
    }
  } catch (_) {
    /* refused — held on the root below */
  }
  if (!r) return false
  r.__addAppPromptHeld = t
  return 'held'
}

// The address without the builder's one-time params → `pathname + search +
// hash`, or null for an unreadable href: `prompt` and `fly` leave the query
// (and `thread` too, with `opts.thread` — a waiting text must not bind another
// app); `prompt` and `fly` leave a param-shaped fragment, every other part of
// it kept as written (`#prompt=…&tab=2` → `#tab=2`; a plain `#main-content`
// is untouched; an emptied fragment drops its `#`).
export function addAppPromptAddress(href, opts) {
  let url
  try {
    url = new URL(String(href || ''))
  } catch (_) {
    return null
  }
  url.searchParams.delete('prompt')
  url.searchParams.delete('fly')
  if (opts && opts.thread) url.searchParams.delete('thread')
  let hash = url.hash
  const frag = hash.replace(/^#/, '')
  if (/(^|&)(prompt|fly)(=|&|$)/.test(frag)) {
    const kept = frag.split('&').filter((part) => {
      const key = part.split('=')[0]
      let name = key
      try {
        name = decodeURIComponent(key)
      } catch (_) {
        name = key
      }
      return part !== '' && name !== 'prompt' && name !== 'fly'
    })
    hash = kept.length ? '#' + kept.join('&') : ''
  }
  return url.pathname + url.search + hash
}

// The builder's MOUNT, before its thread hydrates. Where the builder is the
// route (`opts.url !== false` — the home's Build tab is not: its address is
// the home's), a `?prompt=` / `#prompt=` is parked and the params leave the
// address bar in the SAME call — after the park holds the text, never before.
// A waiting text — this mount's, or one parked earlier (an earlier mount, the
// welcome's Build my app) — unbinds the app the builder holds, so the new
// thread is the one that opens. → true when a text waits for the send.
export function addAppPromptTake(root, opts) {
  const el = this
  const r = root || (el && el.state && el.state.root) || null
  if (!r || typeof r.update !== 'function') return false
  const PARK = 'symbols_add_app_prompt'
  const MAX_AGE_MS = 10 * 60 * 1000
  const w = typeof window !== 'undefined' ? window : null
  let store = null
  try {
    store = w ? w.sessionStorage : null
  } catch (_) {
    store = null
  }
  const useUrl = !(opts && opts.url === false)
  if (useUrl && w && w.location) {
    const got = el.call('addAppPromptRead', w.location.search, w.location.hash) || {}
    if (got.fly) el.call('onboardingNoteFlyArrival', r)
    // nothing to park in: 'held' for this page, the param kept until the send
    const kept = got.text ? el.call('addAppPromptPark', got.text, r) : false
    if (kept === 'parked' || (got.fly && !got.text)) {
      try {
        const next = el.call('addAppPromptAddress', w.location.href, { thread: !!got.text })
        const now = w.location.pathname + w.location.search + w.location.hash
        if (next && next !== now && w.history && typeof w.history.replaceState === 'function') {
          // The shell's query-only address contract (functions/homeBoardUrl.js):
          // no navigation, no router dispatch, no new history entry.
          // frank-allow FA410
          w.history.replaceState(w.history.state, '', next)
        }
      } catch (err) {
        console.warn(
          '[add-app] the prompt link could not leave the address bar:',
          err && err.message
        )
      }
    }
  }
  let waiting = !!r.__addAppPromptHeld
  if (!waiting && store) {
    try {
      const raw = store.getItem(PARK)
      const parked = raw ? JSON.parse(raw) : null
      waiting = !!(
        parked &&
        typeof parked.text === 'string' &&
        parked.text.trim() &&
        Date.now() - Number(parked.at) <= MAX_AGE_MS
      )
    } catch (_) {
      waiting = false
    }
  }
  if (waiting) {
    r.__addAppHydratedThreadId = null
    if (r._addAppProjectId) r.update({ _addAppProjectId: null }, { preventFetch: true })
  }
  return waiting
}

// THE SEND — the builder's mount and every transport change call it. Once the
// transport is open, the waiting text, taken ONE time, opens a NEW build
// thread as its first message: the builder lets go of any app it holds, a
// fresh thread starts (newSession — the history's own "new chat"), and the
// text goes as the person's message (sendPrompt, the builder's scope).
//
// THE ONE BUILD PLACE is asked here, by the flush itself, for every caller
// (functions/workspaceSetup.js workspaceSetupOwnsBuild — while a setup job is
// this workspace's, its first thread is the only place the idea is built):
//   true   the setup owns the build — the waiting text is DROPPED (the park,
//          the page's hold, and the param a page-held text kept in the
//          address bar), before the transport is asked: the arrival mark is
//          written a few ms before the park, so a reload at the mark's
//          10-minute edge would find the mark expired and the park alive, and
//          send it as a second build in a new thread
//   null   not known yet — the park is kept, nothing is sent
//   false  no setup to build in (or no setup module in this shell) — sent
// → a promise of true when it sent.
export async function addAppPromptFlush(root) {
  const el = this
  const r = root || (el && el.state && el.state.root) || null
  if (!r || typeof r.update !== 'function') return false
  const PARK = 'symbols_add_app_prompt'
  const MAX_AGE_MS = 10 * 60 * 1000
  const w = typeof window !== 'undefined' ? window : null
  const fns = (el && el.context && el.context.functions) || {}
  const owns =
    typeof fns.workspaceSetupOwnsBuild === 'function'
      ? el.call('workspaceSetupOwnsBuild', r)
      : false
  if (owns === null) return false
  if (owns === true) {
    const held = !!r.__addAppPromptHeld
    r.__addAppPromptHeld = null
    try {
      const store = w ? w.sessionStorage : null
      if (store) store.removeItem(PARK)
    } catch (_) {
      /* nothing could be parked */
    }
    if (held && w && w.location) {
      try {
        const next = el.call('addAppPromptAddress', w.location.href, { thread: false })
        const now = w.location.pathname + w.location.search + w.location.hash
        if (next && next !== now && w.history && typeof w.history.replaceState === 'function') {
          // frank-allow FA410
          w.history.replaceState(w.history.state, '', next)
        }
      } catch (_) {
        /* the address keeps the param; a reload is an arrival the setup owns */
      }
    }
    return false
  }
  if (r.bridgeStatus !== 'open') return false
  let text = ''
  let fromPage = false
  try {
    const store = w ? w.sessionStorage : null
    const raw = store ? store.getItem(PARK) : null
    if (raw) store.removeItem(PARK)
    const parked = raw ? JSON.parse(raw) : null
    if (parked && typeof parked.text === 'string' && Date.now() - Number(parked.at) <= MAX_AGE_MS) {
      text = parked.text.trim()
    }
  } catch (_) {
    text = ''
  }
  if (!text && r.__addAppPromptHeld) {
    text = String(r.__addAppPromptHeld).trim()
    fromPage = true
  }
  r.__addAppPromptHeld = null
  if (!text) return false
  r.__addAppHydratedThreadId = null
  r.update(
    { _addAppProjectId: null, _addAppRendered: false, _addAppUnbackedEntities: [] },
    { preventFetch: true }
  )
  try {
    await el.call('newSession', 'addapp')
  } catch (err) {
    console.warn('[add-app] a new build thread did not start:', err && err.message)
  }
  el.call('sendPrompt', text, 'addapp')
  // The page-held text is sent: its param leaves the address bar now — the
  // query's AND the fragment's (a held welcome idea never had one, and an
  // unchanged address writes no history).
  if (fromPage && w && w.location) {
    try {
      const next = el.call('addAppPromptAddress', w.location.href, { thread: false })
      const now = w.location.pathname + w.location.search + w.location.hash
      if (next && next !== now && w.history && typeof w.history.replaceState === 'function') {
        // frank-allow FA410
        w.history.replaceState(w.history.state, '', next)
      }
    } catch (_) {
      /* the text is sent; the address keeps a param a reload would read */
    }
  }
  return true
}

// THE PENDING IDEA — the app idea a person typed in Fly's plan before the
// workspace existed, held on the workspace (`settings.pendingIdea = { v: 1,
// text, at, via }`, server WorkspaceController validatePendingIdea) until
// Create opens with it ONCE (persona round 4, 2026-10-28: aiko applied her
// plan to an existing workspace and tomas signed in at /login — the idea rode
// only the hand-over URL, so both found Create empty).
//
// The builder's mount calls it after addAppPromptTake. The server is the
// judge of who takes it: the idea is CLEARED first (`pendingIdea: null`, an
// owner/admin write), and only a clear that succeeded parks the text and
// sends it — the same park and flush a `?prompt=` link uses, so the setup
// guard and the one-send rule hold. A member who may not clear it never gets
// the owner's idea; a text already waiting (the URL's, which wins) only
// clears it. → a promise of true when the idea was taken.
export async function addAppPendingIdeaTake(root) {
  const el = this
  const r = root || (el && el.state && el.state.root) || null
  if (!r || typeof r.update !== 'function' || !r.workspaceReady) return false
  const ws = r.workspace
  const wsId = String((ws && (ws.id || ws._id)) || '')
  const idea = ws && ws.settings && ws.settings.pendingIdea
  const text =
    idea && idea.v === 1 && typeof idea.text === 'string' ? idea.text.trim().slice(0, 2000) : ''
  if (!wsId || !text) return false
  // One take per idea per page, even where the held record keeps a stale copy.
  const stamp = wsId + '|' + String(idea.at || '')
  if (r.__addAppPendingIdeaFlight === wsId || r.__addAppPendingIdeaTaken === stamp) return false
  const fns = (el && el.context && el.context.functions) || {}
  if (typeof fns.currentUserIsAdmin !== 'function' || !el.call('currentUserIsAdmin', r)) {
    return false
  }
  let sdk = null
  if (typeof fns.getSDK === 'function') {
    try {
      sdk = el.call('getSDK')
    } catch (_) {
      sdk = null
    }
  }
  if (!sdk || sdk.isNullClient === true || typeof sdk.updateWorkspaceSettings !== 'function') {
    return false
  }
  const PARK = 'symbols_add_app_prompt'
  const w = typeof window !== 'undefined' ? window : null
  let waiting = !!r.__addAppPromptHeld
  if (!waiting) {
    try {
      const store = w ? w.sessionStorage : null
      waiting = !!(store && store.getItem(PARK))
    } catch (_) {
      waiting = false
    }
  }
  r.__addAppPendingIdeaFlight = wsId
  let cleared = false
  try {
    const res = await sdk.updateWorkspaceSettings(wsId, { pendingIdea: null })
    cleared = !(res && res.success === false)
  } catch (err) {
    console.warn('[add-app] the waiting idea could not be taken:', err && err.message)
  } finally {
    r.__addAppPendingIdeaFlight = null
  }
  if (!cleared) return false
  r.__addAppPendingIdeaTaken = stamp
  // The held record forgets it too, so no later mount takes it again.
  const held = r.workspace
  if (held && String(held.id || held._id || '') === wsId && held.settings) {
    const { pendingIdea, ...rest } = held.settings
    if (typeof fns.setActiveWorkspaceRecord === 'function') {
      el.call(
        'setActiveWorkspaceRecord',
        r,
        { ...held, settings: rest },
        { reason: 'add-app:pendingIdea' }
      )
    }
  }
  if (waiting) return false
  if (!el.call('addAppPromptPark', text, r)) return false
  await el.call('addAppPromptFlush', r)
  return true
}
